// ============================================================================
// SINGLE TASK GUARD
// Phase 2A: Enforces single-task lockdown mode
// Prevents navigation outside the assigned task
// ============================================================================

import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAccessResolver } from "@/hooks/useAccessResolver";
import { isRouteAllowed } from "@/lib/routeFromAccess";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

interface SingleTaskGuardProps {
  children: React.ReactNode;
}

/**
 * Guard component that enforces single-task lockdown
 * - Disables navigation outside the task
 * - Redirects unauthorized routes back to the task
 * - Blocks browser back navigation
 * - Blocks manual URL entry
 * - Hides sidebar/navigation (handled by layout components)
 */
export const SingleTaskGuard = ({ children }: SingleTaskGuardProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: access, isLoading } = useAccessResolver();
  const { signOut } = useAuth();
  const allowedPathRef = useRef<string | null>(null);

  // Store the allowed path when single-task access is detected
  useEffect(() => {
    if (!isLoading && access?.type === "single_task") {
      allowedPathRef.current = `/clean/${access.propertyId}`;
    }
  }, [access, isLoading]);

  // Enforce route lockdown - blocks browser back navigation and manual URL entry
  useEffect(() => {
    if (isLoading) return;

    // Only enforce lockdown for single-task access
    if (access?.type !== "single_task") {
      allowedPathRef.current = null;
      return;
    }

    const taskRoute = `/clean/${access.propertyId}`;
    const isCurrentRouteAllowed = isRouteAllowed(location.pathname, access);

    // If route is not allowed, redirect back to the task
    // This handles: browser back navigation, manual URL entry, programmatic navigation
    if (!isCurrentRouteAllowed) {
      navigate(taskRoute, { replace: true });
    }

    // If access becomes invalid mid-session (e.g., invite revoked), force logout
    if (access.type === "invite_invalid") {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[SingleTaskGuard] Access became invalid mid-session, forcing logout");
      }
      signOut();
      navigate("/login", { replace: true });
    }
  }, [access, isLoading, location.pathname, navigate, signOut]);

  // Block browser back navigation using popstate — except allow back to schedule
  useEffect(() => {
    if (!access || access.type !== "single_task") {
      return;
    }

    const taskRoute = `/clean/${access.propertyId}`;

    const handlePopState = () => {
      // After back, URL may already be /worker/schedule — allow it so cleaner can return to task list
      const pathname = window.location.pathname;
      if (pathname === "/worker/schedule" || pathname.startsWith("/worker/schedule")) {
        return;
      }
      navigate(taskRoute, { replace: true });
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [access, navigate]);

  // If loading, show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p>Loading...</p>
      </div>
    );
  }

  // If not single-task access, render children normally
  if (access?.type !== "single_task") {
    return <>{children}</>;
  }

  // If route is not allowed, show blocking screen (should be caught by useEffect redirect)
  if (!isRouteAllowed(location.pathname, access)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Access Restricted</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              You have access to a single task only. Redirecting...
            </p>
            <Button
              onClick={() => navigate(`/clean/${access.propertyId}`, { replace: true })}
              className="w-full"
            >
              Go to Task
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Single-task access and route is allowed - render children
  return <>{children}</>;
};

